
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./data_invoice_for_trans_invoice.css";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Modal, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import axios from "axios";
import useSWR, { mutate } from "swr";

interface DataType {
    key: React.Key;
    receiptNo: string;
    invoiceNo: string;
    invoiceDate: string;
    customerCode: string;
    customerName: string;
    balance: number;
    amountPayment: number;
    invoiceAmount: number;
    transactionTo: string;
    warehouse: string;
    top: string;
    verifikasi: string;
    warehouseCode: string;
}

interface ApiDataType {
    id: number;
    receipt_no: string;
    no_invoice: string;
    invoice_date: string;
    customer_code: string;
    customer_name: string;
    invoice_amount: string;
    saldo_payment: string;
    saldo_akhir: string;
    transaction_to: string;
    warehouse: string;
    warehouse_name: string;
    top: string;
    verifikasi: string;
}

type DataIndex = keyof DataType;

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const fetcher = (url: string) => fetch(url).then(res => res.json());
const formatRupiah = (value: number) => {
    if (typeof value !== 'number') {
        return value;
    }
    return new Intl.NumberFormat('id-ID').format(value);
};

interface DataInvoiceProps {
    onRowSelect: (record: DataType) => void;
}

const DataInvoiceForTransInvoice: React.FC<DataInvoiceProps> = ({ onRowSelect }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };


    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [selectedRowKey, setSelectedRowKey] = useState<React.Key | null>(null);
    const { data: apiData, error, isLoading } = useSWR<ApiDataType[]>(`${backendUrl}/api/transaction-invoices-data`, fetcher);

    const dataSource = useMemo(() => {
        if (!apiData) return [];
        return apiData.map(item => ({
            key: item.id,
            receiptNo: item.receipt_no,
            invoiceNo: item.no_invoice,
            invoiceDate: item.invoice_date ? new Date(item.invoice_date).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-',
            customerCode: item.customer_code,
            customerName: item.customer_name,
            invoiceAmount: parseFloat(item.invoice_amount) || 0,
            balance: parseFloat(item.saldo_akhir) || 0,
            amountPayment: parseFloat(item.saldo_payment) || 0,
            transactionTo: item.transaction_to,
            warehouse: item.warehouse_name,
            top: item.top,
            verifikasi: item.verifikasi,
            warehouseCode: item.warehouse,
        }));
    }, [apiData]);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };


    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };


    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => {
            const recordValue = record[dataIndex];

            if (recordValue === undefined || recordValue === null) return false;
            return recordValue.toString().toLowerCase().includes((value as string).toLowerCase());
        },
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Receipt No',
            dataIndex: 'receiptNo',
            key: 'receiptNo',
            width: '15%',
            ...getColumnSearchProps('receiptNo'),
            sorter: (a, b) => a.receiptNo.localeCompare(b.receiptNo),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Invoice No',
            dataIndex: 'invoiceNo',
            key: 'invoiceNo',
            width: '15%',
            ...getColumnSearchProps('invoiceNo'),
            sorter: (a, b) => a.invoiceNo.localeCompare(b.invoiceNo),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Invoice Date',
            dataIndex: 'invoiceDate',
            key: 'invoiceDate',
            width: '15%',
            ...getColumnSearchProps('invoiceDate'),
            sorter: (a, b) => a.invoiceDate.localeCompare(b.invoiceDate),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Customer Code',
            dataIndex: 'customerCode',
            key: 'customerCode',
            width: '15%',
            ...getColumnSearchProps('customerCode'),
            sorter: (a, b) => a.customerCode.localeCompare(b.customerCode),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Customer Name',
            dataIndex: 'customerName',
            key: 'customerName',
            width: '25%',
            ...getColumnSearchProps('customerName'),
            sorter: (a, b) => a.customerName.localeCompare(b.customerName),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Invoice Amount',
            dataIndex: 'invoiceAmount',
            key: 'invoiceAmount',
            width: '15%',
            ...getColumnSearchProps('invoiceAmount'),
            sorter: (a, b) => a.invoiceAmount - b.invoiceAmount,
            sortDirections: ['descend', 'ascend'],
            render: (text: number) => {
                const formattedText = formatRupiah(text);
                return searchedColumn === 'invoiceAmount' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={formattedText}
                    />
                ) : (
                    formattedText
                );
            },
        },
        {
            title: 'Balance',
            dataIndex: 'balance',
            key: 'balance',
            width: '15%',
            ...getColumnSearchProps('balance'),
            sorter: (a, b) => a.balance - b.balance,
            sortDirections: ['descend', 'ascend'],
            render: (text: number) => {
                const formattedText = formatRupiah(text);
                return searchedColumn === 'balance' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={formattedText}
                    />
                ) : (
                    formattedText
                );
            },
        },
        {
            title: 'Amount Payment',
            dataIndex: 'amountPayment',
            key: 'amountPayment',
            width: '15%',
            ...getColumnSearchProps('amountPayment'),
            sorter: (a, b) => a.amountPayment - b.amountPayment,
            sortDirections: ['descend', 'ascend'],
            render: (text: number) => {
                const formattedText = formatRupiah(text);
                return searchedColumn === 'amountPayment' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={formattedText}
                    />
                ) : (
                    formattedText
                );
            },
        },
    ];

    const handleRowClick = (record: DataType) => {
        if (record.verifikasi === '3') {
            Modal.warning({
                title: 'Peringatan',
                content: 'Data belum terverifikasi',
                onOk() { },
            });
            setSelectedRowKey(null);
        } else {
            onRowSelect(record);
            setSelectedRowKey(record.key);
            mutate(`${backendUrl}/api/transaction-invoices-data`);
        }
    };

    return (

        <div id="ilqt" style={transformStyle}>
            <div className="gjs-grid-row" id="iml9">
                <div className="gjs-grid-column" id="is2u">
                    { }
                    <Table<DataType>
                        columns={columns}
                        dataSource={dataSource}
                        loading={isLoading}
                        pagination={{ pageSize: 10 }}
                        onRow={(record) => {
                            return {
                                onClick: () => {
                                    handleRowClick(record);
                                },
                                className: record.key === selectedRowKey ? 'selected-row' : '',
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataInvoiceForTransInvoice;